// Budget load grid: columns, value normalization and validation.
//
// The grid keeps the layout ModalConfirm ("budget") sends to the backend:
// 19 columns per row, catalogs as "code-name" text, year and amounts as
// numbers. Everything here is pure so the screen and the tests share it.

export interface CatalogoBudget {
  // "code-name" labels, as stored in the grid
  etiquetas: string[];
  // code -> label (codes are unique in every catalog)
  porCodigo: Map<string, string>;
  etiquetasSet: Set<string>;
}

export const COL = {
  CENTRO: 0,
  CUENTA: 1,
  ITEM: 2,
  NATURALEZA: 3,
  ANIO: 4,
  TOTAL: 5,
  OBS: 6,
  MES1: 7,
  MES12: 18,
} as const;

export const MESES_CORTOS = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];

export const COL_HEADERS = [
  "Centro de costo",
  "Cuenta contable",
  "Item contable",
  "Naturaleza",
  "Año",
  "Ppto. Año",
  "Observaciones",
  ...[
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ].map((m) => `Ppto. ${m}`),
];

export const NUM_COLS = COL_HEADERS.length; // 19

export const esColMonto = (c: number) =>
  c === COL.TOTAL || (c >= COL.MES1 && c <= COL.MES12);

export const esColCatalogo = (c: number) =>
  c >= COL.CENTRO && c <= COL.NATURALEZA;

/* ------------------------------------------------------------------ */
/* Catalogs                                                             */
/* ------------------------------------------------------------------ */

export function crearCatalogo<T>(
  filas: T[] | null | undefined,
  codigo: (f: T) => unknown,
  nombre: (f: T) => unknown,
): CatalogoBudget {
  const porCodigo = new Map<string, string>();
  (filas ?? []).forEach((f) => {
    const c = String(codigo(f) ?? "").trim();
    if (!c || porCodigo.has(c)) return;
    porCodigo.set(c, `${c}-${String(nombre(f) ?? "").trim()}`);
  });
  const etiquetas = Array.from(porCodigo.values());
  return { etiquetas, porCodigo, etiquetasSet: new Set(etiquetas) };
}

/**
 * Completes a catalog cell: the exact label, a bare code ("5101") or
 * "code - name" with other spacing become the catalog label. Anything
 * else is returned as typed (and then flagged).
 */
export function normalizarCatalogo(valor: unknown, cat: CatalogoBudget) {
  if (valor === null || valor === undefined) return valor;
  const texto = String(valor).trim();
  if (!texto) return "";
  if (cat.etiquetasSet.has(texto)) return texto;
  const m = /^(\d+)\s*(?:-|$)/.exec(texto);
  if (m) {
    // "0042" also finds code "42"
    return (
      cat.porCodigo.get(m[1]) ??
      cat.porCodigo.get(String(Number(m[1]))) ??
      texto
    );
  }
  return texto;
}

/* ------------------------------------------------------------------ */
/* Amounts                                                              */
/* ------------------------------------------------------------------ */

/**
 * Reads an amount typed or pasted from Excel (es-CL or plain):
 *  "1.234.567" / "1.234" -> thousands separators (1234567 / 1234)
 *  "1.234.567,89" / "1234,5" -> comma decimals
 *  "1234567.89" -> dot decimals (not a thousands pattern)
 *  "$ 1.000" -> 1000
 * Returns null for empty, NaN when it is not a valid non-negative amount.
 */
export function parseMonto(valor: unknown): number | null {
  if (valor === null || valor === undefined) return null;
  if (typeof valor === "number") {
    return Number.isFinite(valor) && valor >= 0 ? valor : NaN;
  }
  const t = String(valor).replace(/[$\s]/g, "");
  if (!t) return null;
  if (t.includes(",")) {
    if (!/^(\d{1,3}(\.\d{3})+|\d+),\d{1,4}$/.test(t)) return NaN;
    return Number(t.replace(/\./g, "").replace(",", "."));
  }
  if (/^\d{1,3}(\.\d{3})+$/.test(t)) return Number(t.replace(/\./g, ""));
  if (/^\d+(\.\d{1,4})?$/.test(t)) return Number(t);
  return NaN;
}

export const formatMonto = (v: number) =>
  v.toLocaleString("es-CL", { maximumFractionDigits: 4 });

/* ------------------------------------------------------------------ */
/* Validation                                                           */
/* ------------------------------------------------------------------ */

export type Severidad = "error" | "aviso";

export interface Problema {
  fila: number; // physical row
  col: number;
  severidad: Severidad;
  mensaje: string;
}

export interface ResumenBudget {
  filas: number; // rows with data
  totalAnual: number;
  totalMeses: number;
  centros: number;
  anios: number[];
  // Rows whose annual total differs from the sum of their months
  descuadres: number;
  // Rows with empty month cells / empty annual total
  mesesVacios: number;
  totalesVacios: number;
}

export interface ResultadoValidacion {
  problemas: Problema[];
  // "row:col" -> severity, for the cell renderer
  celdas: Map<string, Severidad>;
  errores: number;
  avisos: number;
  resumen: ResumenBudget;
}

export interface CatalogosBudget {
  centros: CatalogoBudget;
  cuentas: CatalogoBudget;
  items: CatalogoBudget;
  naturalezas: CatalogoBudget;
}

const vacio = (v: unknown) =>
  v === null || v === undefined || String(v).trim() === "";

export const filaVacia = (fila: unknown[] | null | undefined) =>
  !fila || fila.every(vacio);

// Free text: letters (accents, ñ), digits and common punctuation
const OBS_VALIDA = /^[\p{L}\p{N}\s.,;:()\-_/%#&+°'"¿?¡!]*$/u;

// 1 peso tolerance between the annual total and the sum of the months
const TOLERANCIA = 1;

export function validarPresupuesto(
  datos: unknown[][] | null | undefined,
  cat: CatalogosBudget,
): ResultadoValidacion {
  const problemas: Problema[] = [];
  const add = (
    fila: number,
    col: number,
    severidad: Severidad,
    mensaje: string,
  ) => problemas.push({ fila, col, severidad, mensaje });

  const resumen: ResumenBudget = {
    filas: 0,
    totalAnual: 0,
    totalMeses: 0,
    centros: 0,
    anios: [],
    descuadres: 0,
    mesesVacios: 0,
    totalesVacios: 0,
  };
  const centros = new Set<string>();
  const anios = new Set<number>();
  const claves = new Map<string, number[]>();

  const catalogoDe: Record<number, [CatalogoBudget, string]> = {
    [COL.CENTRO]: [cat.centros, "centro de costo"],
    [COL.CUENTA]: [cat.cuentas, "cuenta contable"],
    [COL.ITEM]: [cat.items, "item contable"],
    [COL.NATURALEZA]: [cat.naturalezas, "naturaleza"],
  };

  (datos ?? []).forEach((fila, r) => {
    if (filaVacia(fila)) return;
    resumen.filas++;

    // Catalogs
    for (let c = COL.CENTRO; c <= COL.NATURALEZA; c++) {
      const v = fila[c];
      const [catalogo, nombre] = catalogoDe[c];
      if (vacio(v)) add(r, c, "error", `Falta ${nombre}`);
      else if (!catalogo.etiquetasSet.has(String(v).trim()))
        add(r, c, "error", `"${v}" no existe en el catálogo de ${nombre}`);
    }
    if (!vacio(fila[COL.CENTRO])) centros.add(String(fila[COL.CENTRO]));

    // Year
    const anioTxt = String(fila[COL.ANIO] ?? "").trim();
    if (!anioTxt) add(r, COL.ANIO, "error", "Falta el año");
    else if (!/^20\d{2}$/.test(anioTxt))
      add(r, COL.ANIO, "error", `Año "${anioTxt}" inválido (ej: 2027)`);
    else anios.add(Number(anioTxt));

    // Observations (optional)
    const obs = fila[COL.OBS];
    if (!vacio(obs) && !OBS_VALIDA.test(String(obs)))
      add(
        r,
        COL.OBS,
        "error",
        "Observaciones con caracteres no permitidos (< > { } [ ] = ...)",
      );

    // Amounts
    let sumaMeses = 0;
    let mesesValidos = 0;
    let vacios = 0;
    for (let c = COL.MES1; c <= COL.MES12; c++) {
      const m = parseMonto(fila[c]);
      if (m === null) {
        vacios++;
        add(
          r,
          c,
          "error",
          `Falta ${COL_HEADERS[c]} (ingresa 0 si no hay presupuesto)`,
        );
      } else if (Number.isNaN(m)) {
        add(
          r,
          c,
          "error",
          `${COL_HEADERS[c]}: "${fila[c]}" no es un monto válido`,
        );
      } else {
        sumaMeses += m;
        mesesValidos++;
      }
    }
    if (vacios) resumen.mesesVacios++;

    const total = parseMonto(fila[COL.TOTAL]);
    if (total === null) {
      resumen.totalesVacios++;
      add(r, COL.TOTAL, "error", "Falta Ppto. Año");
    } else if (Number.isNaN(total)) {
      add(
        r,
        COL.TOTAL,
        "error",
        `Ppto. Año: "${fila[COL.TOTAL]}" no es un monto válido`,
      );
    } else {
      resumen.totalAnual += total;
      if (mesesValidos === 12 && Math.abs(total - sumaMeses) > TOLERANCIA) {
        resumen.descuadres++;
        add(
          r,
          COL.TOTAL,
          "aviso",
          `Ppto. Año (${formatMonto(total)}) no cuadra con la suma de los meses (${formatMonto(sumaMeses)})`,
        );
      }
    }
    resumen.totalMeses += sumaMeses;

    // Duplicates: same cost center + accounts + year
    const clave = [0, 1, 2, 3, 4]
      .map((c) => String(fila[c] ?? "").trim())
      .join("|");
    if (clave.split("|").every(Boolean)) {
      claves.set(clave, [...(claves.get(clave) ?? []), r]);
    }
  });

  claves.forEach((filas) => {
    if (filas.length < 2) return;
    filas.forEach((r) =>
      add(
        r,
        COL.CENTRO,
        "error",
        `Línea duplicada: mismo centro, cuentas y año en las filas ${filas
          .map((f) => f + 1)
          .join(", ")}`,
      ),
    );
  });

  resumen.centros = centros.size;
  resumen.anios = Array.from(anios).sort();

  // One severity per cell (error wins)
  const celdas = new Map<string, Severidad>();
  problemas.forEach((p) => {
    const k = `${p.fila}:${p.col}`;
    if (celdas.get(k) !== "error") celdas.set(k, p.severidad);
  });
  problemas.sort((a, b) =>
    a.severidad !== b.severidad
      ? a.severidad === "error"
        ? -1
        : 1
      : a.fila - b.fila || a.col - b.col,
  );

  return {
    problemas,
    celdas,
    errores: problemas.filter((p) => p.severidad === "error").length,
    avisos: problemas.filter((p) => p.severidad === "aviso").length,
    resumen,
  };
}
